import i18n from "i18next";
/**
 * The app-bar crumb: `L › Tool › item` (navigation handoff §1).
 *
 * It names where you are, which was the old sidebar's job, in the same place
 * on every surface; the L before it is the Lilia menu. In the editor the tool
 * is replaced by the **origin crumb** (§5): the exact view the paper was
 * opened from — *Documents · Drafts*, *Tables · Has problems* — which returns
 * there, scroll and all. A paper opened from a link shows *Documents*.
 *
 * In the editor, a sheet over the paper (a figure's split view) adds itself
 * after the origin: `… › Figure`.
 *
 * testids: `app-crumb` · `editor-origin-crumb` · `app-crumb-item`.
 */
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";
import { testIds } from "@/testIds";
import { toolForPath } from "@/lib/tools";
import { originFor, toolEntry } from "@/lib/viewMemory";

// ── the item, named by the page that shows it ────────────────────────────────
let crumbItem: string | null = null;
const listeners = new Set<() => void>();
function setCrumbItem(name: string | null) {
  if (crumbItem === name) return;
  crumbItem = name;
  listeners.forEach((l) => l());
}
/** A page inside a tool names the thing it shows — `L › Tables › Top-1
 *  accuracy` — for as long as it's showing it. */
export function useAppCrumbItem(name: string | null | undefined) {
  useEffect(() => {
    setCrumbItem(name || null);
    return () => setCrumbItem(null);
  }, [name]);
}
const useCrumbItem = () =>
  useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => crumbItem);

/** Places that aren't tools but still say where you are. */
const PLACES: [prefix: string, label: string][] = [
  ["/trash", "trash"],
  ["/teams", "teams"],
  ["/playgrounds", "playgrounds"],
  ["/epub", "ePub"],
  ["/editor", "editor"],
];

const Sep = () => (
  <span aria-hidden className="shrink-0 px-0.5 text-[13px] text-[var(--lilia-text-muted)]">›</span>
);

export function AppCrumb() {
  const { pathname, key } = useLocation();
  const navigate = useNavigate();
  const item = useCrumbItem();

  const inEditor = /^\/d\/[^/]+\/?$/.test(pathname);
  // Fixed per visit (see originFor): the view this paper was opened from.
  const origin = useMemo(() => (inEditor ? originFor(`${pathname}#${key}`) : null), [inEditor, pathname, key]);

  const tool = toolForPath(pathname);
  const placeKey = PLACES.find(([p]) => pathname === p || pathname.startsWith(p + "/"))?.[1];
  // "ePub" is a name; the others are words in the interface's language.
  const place = pathname === "/" ? i18n.t("shell:home.label") : placeKey && placeKey !== "ePub" ? i18n.t(`shell:crumb.${placeKey}`) : placeKey;

  if (inEditor && origin) {
    return (
      <nav data-testid={testIds.appCrumb.root} aria-label={i18n.t("shell:crumb.where")} className="flex min-w-0 shrink items-center gap-1">
        <Sep />
        <button
          type="button"
          data-testid={testIds.appCrumb.editorOrigin}
          data-origin={origin.path}
          onClick={() => navigate(origin.path)}
          title={i18n.t("shell:crumb.backTo", { label: origin.label })}
          className="inline-flex h-[26px] min-w-0 max-w-[260px] items-center truncate rounded-[6px] bg-[var(--lilia-bg-secondary)] px-2 text-[12.5px] text-[var(--lilia-text-secondary)] transition-colors hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)]"
        >
          <span className="truncate">{origin.label}</span>
        </button>
        {/* What the editor has open over the paper, e.g. a figure's split view. */}
        {item && (
          <>
            <Sep />
            <span data-testid={testIds.appCrumb.item} className="min-w-0 max-w-[200px] truncate px-1 text-[13px] font-semibold text-[var(--lilia-text)]">
              {item}
            </span>
          </>
        )}
      </nav>
    );
  }

  const name = tool?.name ?? place;
  if (!name) return <nav data-testid={testIds.appCrumb.root} aria-label={i18n.t("shell:crumb.where")} className="hidden" />;

  // The tool's name goes back to the tool — to its last view — whenever
  // you're somewhere under it rather than on it.
  const atRoot = !tool || pathname === tool.path;
  const showItem = !!item && !atRoot;
  return (
    <nav data-testid={testIds.appCrumb.root} aria-label={i18n.t("shell:crumb.where")} className="flex min-w-0 shrink items-center gap-1">
      <Sep />
      {tool && !atRoot ? (
        <button
          type="button"
          data-testid={testIds.appCrumb.tool}
          onClick={() => navigate(toolEntry(tool.key, tool.path))}
          className={
            "truncate rounded-[5px] px-1 py-0.5 text-[13px] transition-colors hover:bg-[var(--lilia-hover)] " +
            (showItem ? "text-[var(--lilia-text-secondary)]" : "font-semibold text-[var(--lilia-text)]")
          }
        >
          {name}
        </button>
      ) : (
        <span data-testid={testIds.appCrumb.tool} className="truncate px-1 text-[13px] font-semibold text-[var(--lilia-text)]">{name}</span>
      )}
      {showItem && (
        <>
          <Sep />
          <span className="min-w-0 max-w-[280px] truncate px-1 text-[13px] font-semibold text-[var(--lilia-text)]">{item}</span>
        </>
      )}
    </nav>
  );
}
